import React from "react";
import { Card, CardHeader, Grid, GridColumn, Input } from "@sumprio/ui";
import { SearchNormalIcon } from "@sumprio/ui/icons";
import { RefreshButton } from "components/common";

export interface ListPageTableCardProps {
  searchPlaceholder?: string;
  searchValue: string;
  onSearchChange: (_value: string) => void;
  onRefresh: () => void | Promise<void>;
  headerLeftContent?: React.ReactNode;
  headerRightContent?: React.ReactNode;
  /** Rendered after the refresh control (e.g. primary action button). */
  headerEndContent?: React.ReactNode;
  belowHeader?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  embedded?: boolean;
  fillViewport?: boolean;
}

export default function ListPageTableCard({
  searchPlaceholder = "Search...",
  searchValue,
  onSearchChange,
  onRefresh,
  headerLeftContent,
  headerRightContent,
  headerEndContent,
  belowHeader,
  children,
  className,
  embedded = false,
  fillViewport = false,
}: ListPageTableCardProps) {
  const headerContent = (
    <Grid
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      gap="md"
      width="100%"
    >
      <GridColumn
        span={5}
        spanTablet={12}
        spanMobile={4}
        display="flex"
        alignItems="center"
        gap="md"
      >
        <Grid display="flex" flexWrap="wrap" alignItems="center" gap="md" width="100%">
          <GridColumn span={12} spanTablet={headerLeftContent ? 8 : 12} spanMobile={4}>
            <Input
              placeholder={searchPlaceholder}
              leadingIcon={<SearchNormalIcon />}
              value={searchValue}
              onChange={(e) =>
                onSearchChange((e.target as HTMLInputElement).value ?? e.currentTarget?.value)
              }
              clearable={!!searchValue}
              width="full"
            />
          </GridColumn>
          {headerLeftContent && (
            <GridColumn span={12} spanTablet={8} spanMobile={4}>
              {headerLeftContent}
            </GridColumn>
          )}
        </Grid>
      </GridColumn>
      <GridColumn
        span={7}
        spanTablet={12}
        spanMobile={4}
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
        gap="sm"
        flexWrap="wrap"
      >
        {headerRightContent}
        <RefreshButton onRefresh={onRefresh} />
        {headerEndContent}
      </GridColumn>
    </Grid>
  );

  if (embedded) {
    return (
      <>
        <Grid padding="md" display="flex" flexDirection="column" gap="sm">
          {headerContent}
          {belowHeader}
        </Grid>
        {children}
      </>
    );
  }

  const fillClass = fillViewport ? "list-page-table-card--fill" : undefined;
  const gridClass = [className, fillClass].filter(Boolean).join(" ");

  return (
    <Grid
      marginTop="lg"
      className={gridClass || undefined}
      display={fillViewport ? "flex" : undefined}
      flexDirection={fillViewport ? "column" : undefined}
      flex={fillViewport ? 1 : undefined}
      minHeight={fillViewport ? 0 : undefined}
      width="100%"
    >
      <GridColumn
        span={12}
        display={fillViewport ? "flex" : undefined}
        flexDirection={fillViewport ? "column" : undefined}
        flex={fillViewport ? 1 : undefined}
        minHeight={fillViewport ? 0 : undefined}
        width="100%"
      >
        <Card
          className={["list-table-card", fillViewport ? "list-table-card--fill-viewport" : ""]
            .filter(Boolean)
            .join(" ")}
          style={{ overflow: "visible" }}
        >
          <CardHeader>
            {headerContent}
            {belowHeader}
          </CardHeader>
          {fillViewport ? <div className="list-table-card__body-fill">{children}</div> : children}
        </Card>
      </GridColumn>
    </Grid>
  );
}
